import { createContext, useDisclosure } from '@hover/blueprint';
import type {
  EmailMessage,
  LoadedMessage,
} from '@hover/embed/post-message-api';
import type { PropsWithChildren } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import type { PublicAiOriginalImageFragment } from 'src/shared/graphql/generated';
import {
  useAiEmbeddedDesignerConfigurationQuery,
  useAuthenticationInfoLazyQuery,
  usePublicOrgInfoQuery,
} from 'src/shared/graphql/generated';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { useQueryParam } from 'src/shared/hooks/useQueryParam';
import type { CreateLeadResponse } from 'src/shared/requests/createLead';

type EmbedDisclosure = Pick<
  ReturnType<typeof useDisclosure>,
  'isOpen' | 'onOpen' | 'onClose'
>;

/**
 * The kind of embed that is being used.
 * - `Upload` - The user uploads an image and the result is displayed in the same tab. In this flow
 *    the user can actually interact with the uploaded image in the embed. The user can sign up or
 *    login before uploading an image.
 * - `SampleImageEdit` - The user can interact with the sample images in the embed. The user can sign
 *    up by going to the signup page in a new tab outside of the embed.
 * - `RealtorV0` - Soon to be deprecated. This version allows upload without signup using an anonyomous session.
 */
export enum EmbedKind {
  Upload = 'Upload',
  SampleImageEdit = 'SampleImageEdit',
  RealtorPOC = 'RealtorPOC',
}

export interface EmbedContext {
  isEmbed: boolean;
  embedOrgId?: number;
  embedOrgDisplayName?: string;
  embedConfigId?: number;
  originalImageId?: number;
  leadData: CreateLeadResponse | null;
  cta?: string;
  setLeadData: (value: CreateLeadResponse) => void;
  showLandingPage?: boolean;
  ctaLink?: string;
  leadFormHeader?: string;
  leadFormSubheader?: string;
  hasLeadForm: boolean;
  hasLeadFormFullName: boolean;
  hasLeadFormPhoneNumber: boolean;
  hasLeadFormPostalCode: boolean;
  sendHomeownerEmail: boolean;
  header: string;
  subheader: string;
  userEmail?: string;
  embeddedAt?: string;
  hasAccount: boolean;
  sampleImage?: PublicAiOriginalImageFragment;
  allowUpload: boolean;
  embedKind?: EmbedKind;
  colorMode: 'dark' | 'light';
  leadSheetDisclosure: EmbedDisclosure;
  photoUploadSheetDisclosure: EmbedDisclosure;
  welcomeSheetDisclosure: EmbedDisclosure;
  photoLabelSheetDisclosure: EmbedDisclosure;
}

const defaultValue: EmbedContext = {
  isEmbed: false,
  showLandingPage: undefined,
  originalImageId: undefined,
  leadData: null,
  cta: undefined,
  ctaLink: undefined,
  leadFormHeader: undefined,
  leadFormSubheader: undefined,
  hasLeadForm: true,
  hasLeadFormFullName: true,
  hasLeadFormPhoneNumber: true,
  hasLeadFormPostalCode: true,
  sendHomeownerEmail: true,
  hasAccount: false,
  header: '',
  subheader: '',
  setLeadData: () => {},
  sampleImage: undefined,
  allowUpload: false,
  embedKind: undefined,
  embedOrgId: undefined,
  colorMode: 'dark',
  embedOrgDisplayName: undefined,
  leadSheetDisclosure: {
    onOpen: () => {},
    onClose: () => {},
    isOpen: false,
  },
  photoUploadSheetDisclosure: {
    onOpen: () => {},
    onClose: () => {},
    isOpen: false,
  },
  welcomeSheetDisclosure: {
    onOpen: () => {},
    onClose: () => {},
    isOpen: false,
  },
  photoLabelSheetDisclosure: {
    onOpen: () => {},
    onClose: () => {},
    isOpen: false,
  },
};

const [Provider, useContext] = createContext<EmbedContext | undefined>({
  strict: false,
});

const getAllowedOriginsRegex = () => {
  if (__DEV__) {
    return [/^http:\/\/localhost:5173$/];
  }
  return [
    /^https:\/\/embed\.not-hover\.dev$/,
    /^https:\/\/www\.realtor\.com$/,
    /^https:\/\/.*\.realtor\.com$/,
    /^http:\/\/dev\.realtor\.com$/,
    /^http:\/\/dev\.realtor\.com:3000$/,
    /^https:\/\/.*\.previews\.hover\.to$/,
    /^https:\/\/.*\.pella\.com$/,
    /^https:\/\/.*\.pella\.com:8000$/,
    /^https:\/\/www\.pella\.com$/,
  ];
};

export interface EmbedProviderProps extends PropsWithChildren<{}> {}

export const EmbedProvider = (props: EmbedProviderProps) => {
  const publicIdentifier = useQueryParam('aff_org_id');
  // If an originalImageId is passed into the embed, that should override the configuations
  const customOriginalImageId = useQueryParam('originalImageId') || undefined;
  const embedConfigurationId = useQueryParam('embedConfigId');
  const embeddedAtParam = useQueryParam('embeddedAt', true);
  const colorModeParam = useQueryParam('colorMode');

  const [embedOrgId, setEmbedOrgId] = useState<number | undefined>(undefined);
  const [leadData, setLeadData] = useState<CreateLeadResponse | null>(null);
  const [embedConfigId] = useState<number | undefined>(() =>
    Number(embedConfigurationId),
  );
  // TODO: Migrate this to config, or alternatively remove it when we roll out a new
  // version of the embed landing page.
  const [colorMode] = useState<'dark' | 'light'>(() => {
    // Logically, it would make sense to do this check in the embed script, but
    // we don't want to expose partner urls in the embed script since it's publicly available.
    const useLightMode =
      embeddedAtParam?.includes('realtor') ||
      embedConfigId === 13 ||
      colorModeParam === 'light';

    return useLightMode ? 'light' : 'dark';
  });
  const [embedOrgDisplayName, setEmbedOrgDisplayName] = useState<
    string | undefined
  >(undefined);
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [embeddedAt] = useState<string | undefined>(() => embeddedAtParam);
  const [hasAccount, setHasAccount] = useState<boolean>(false);
  const [queryAuthenticationInfo] = useAuthenticationInfoLazyQuery({
    fetchPolicy: 'network-only',
  });

  const leadSheetDisclosure = useDisclosure();
  const photoUploadSheetDisclosure = useDisclosure();
  const welcomeSheetDisclosure = useDisclosure();
  const photoLabelSheetDisclosure = useDisclosure();

  const { setAffOrgPublicId, isEmbed } = useGlobalStorageContext();

  usePublicOrgInfoQuery({
    variables: {
      publicIdentifier: publicIdentifier || '',
    },
    skip: !publicIdentifier,
    onCompleted: data => {
      if (data?.publicOrgInfo?.id) {
        setEmbedOrgId(data.publicOrgInfo.id);
      }

      if (publicIdentifier) {
        setAffOrgPublicId(publicIdentifier);
      }

      if (data?.publicOrgInfo?.displayName) {
        setEmbedOrgDisplayName(data.publicOrgInfo.displayName);
      }
    },
  });

  const { data } = useAiEmbeddedDesignerConfigurationQuery({
    variables: {
      id: embedConfigId ? `${embedConfigId}` : '',
      onlyFeaturedDisplayImages: true,
    },
    skip: !embedConfigId,
  });

  useEffect(() => {
    if (!isEmbed) return () => {};

    const checkAccountStatus = async (email: string) => {
      const authenticationInfo = await queryAuthenticationInfo({
        variables: { email },
      });
      setHasAccount(!!authenticationInfo.data?.authenticationInfo?.emailInUse);
    };

    const handleUserEmailMessage = (event: MessageEvent<EmailMessage>) => {
      if (getAllowedOriginsRegex().some(regex => regex.test(event.origin))) {
        if (event.data.type === 'email' && event.data.payload?.email) {
          const { email } = event.data.payload;
          setUserEmail(email);
          void checkAccountStatus(email);
          removeEventListener('message', handleUserEmailMessage);
        }
      }
    };

    window.addEventListener('message', handleUserEmailMessage);

    const loadedMessage: LoadedMessage = { type: 'loaded' };

    window.parent.postMessage(loadedMessage, '*');

    return () => {
      window.removeEventListener('message', handleUserEmailMessage);
    };
  }, [isEmbed, queryAuthenticationInfo]);

  const value = useMemo(() => {
    const {
      ctaLink,
      sampleImageIds,
      cta,
      hasLeadForm,
      hasLandingPage,
      hasLeadFormFullName,
      hasLeadFormPhoneNumber,
      hasLeadFormPostalCode,
      leadFormHeader,
      leadFormSubheader,
      sendHomeownerEmail,
      header,
      subheader,
      sampleImages,
      allowUpload,
    } = data?.aiEmbeddedDesignerConfigurations || {};

    let embedKind;

    if (allowUpload) {
      embedKind = EmbedKind.Upload;
    } else if (embedConfigId) {
      embedKind = EmbedKind.SampleImageEdit;
    } else if (isEmbed) {
      embedKind = EmbedKind.RealtorPOC;
    }

    // MEOW-5614 - handle multiple images, not just one sample image
    const originalImageId = customOriginalImageId
      ? Number(customOriginalImageId)
      : sampleImageIds?.[0];

    return {
      isEmbed,
      leadData,
      embedOrgId,
      embedOrgDisplayName,
      embedConfigId,
      setLeadData,
      ctaLink: ctaLink ?? undefined,
      originalImageId,
      cta,
      leadFormHeader,
      leadFormSubheader,
      header: header ?? '',
      subheader: subheader ?? '',
      hasLeadForm: hasLeadForm ?? true,
      hasLeadFormFullName: hasLeadFormFullName ?? true,
      hasLeadFormPhoneNumber: hasLeadFormPhoneNumber ?? true,
      hasLeadFormPostalCode: hasLeadFormPostalCode ?? true,
      showLandingPage: hasLandingPage,
      sendHomeownerEmail: sendHomeownerEmail ?? true,
      userEmail,
      embeddedAt,
      hasAccount,
      sampleImage: sampleImages?.find(img => img.id === originalImageId),
      allowUpload: allowUpload ?? false,
      embedKind,
      colorMode,
      leadSheetDisclosure,
      photoUploadSheetDisclosure,
      photoLabelSheetDisclosure,
      welcomeSheetDisclosure,
    };
  }, [
    data?.aiEmbeddedDesignerConfigurations,
    customOriginalImageId,
    isEmbed,
    leadData,
    embedOrgId,
    embedOrgDisplayName,
    embedConfigId,
    userEmail,
    embeddedAt,
    hasAccount,
    colorMode,
    leadSheetDisclosure,
    photoUploadSheetDisclosure,
    photoLabelSheetDisclosure,
    welcomeSheetDisclosure,
  ]);

  return <Provider value={value} {...props} />;
};

export const useEmbedContext = () => useContext() ?? defaultValue;
